import { supabase } from './supabaseClient';
import { AuthService } from './authService';

export interface LinkedInAuthStatus {
  isConnected: boolean;
  connectedAt?: string;
  email?: string;
  name?: string;
  profileUrl?: string;
}

export class LinkedInAuthService {
  /**
   * Check if the current user has LinkedIn authentication
   */
  static async checkLinkedInAuth(userId?: string): Promise<boolean> {
    try {
      const targetUserId = userId || (await supabase.auth.getUser()).data.user?.id;
      
      if (!targetUserId) {
        return false;
      }

      const { data, error } = await supabase.rpc('check_linkedin_auth', {
        user_id: targetUserId
      });

      if (error) {
        console.error('Error checking LinkedIn auth:', error);
        return false;
      }

      return data || false;
    } catch (error) {
      console.error('Unexpected error checking LinkedIn auth:', error);
      return false;
    }
  }

  /**
   * Get LinkedIn authentication status with details
   */
  static async getLinkedInAuthStatus(userId?: string): Promise<LinkedInAuthStatus> {
    try {
      const targetUserId = userId || (await supabase.auth.getUser()).data.user?.id;
      
      if (!targetUserId) {
        return { isConnected: false };
      }

      // Since we can't access admin APIs from the client, we'll use the RPC function
      // and get basic connection status. For detailed info, we'd need a server endpoint
      const isConnected = await this.checkLinkedInAuth(targetUserId);
      
      if (!isConnected) {
        return { isConnected: false };
      }

      // Get basic user info from the current session if available
      const { data: { user } } = await supabase.auth.getUser();
      
      if (user && user.id === targetUserId) {
        // Try to get LinkedIn info from user metadata if available
        const linkedinData = user.user_metadata;
        
        return {
          isConnected: true,
          connectedAt: user.created_at,
          email: linkedinData?.email,
          name: linkedinData?.name || linkedinData?.full_name,
          profileUrl: linkedinData?.profile_url
        };
      }

      // If we can't get detailed info, just return connected status
      return { isConnected: true };
    } catch (error) {
      console.error('Error getting LinkedIn auth status:', error);
      return { isConnected: false };
    }
  }

  /**
   * Connect LinkedIn account
   */
  static async connectLinkedIn(): Promise<{ success: boolean; error?: any }> {
    try {
      return await AuthService.signInWithProvider('linkedin_oidc');
    } catch (error) {
      console.error('Error connecting LinkedIn:', error);
      return { success: false, error };
    }
  }

  /**
   * Disconnect LinkedIn account
   * Note: Supabase doesn't provide a direct way to unlink OAuth providers,
   * so this is a workaround that removes the identity from the user
   */
  static async disconnectLinkedIn(): Promise<{ success: boolean; error?: any }> {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError || !user) {
        throw new Error('User not authenticated');
      }

      // Since Supabase doesn't provide a direct unlink method,
      // we'll use the admin API to remove the identity
      // This requires admin privileges, so in a real app you'd need a server endpoint
      
      // For now, we'll simulate the disconnect by showing a message to the user
      // In a production app, you'd need to implement this on the server side
      console.warn('LinkedIn disconnect requires server-side implementation');
      
      // Alternative approach: Clear local session and force re-authentication
      // This doesn't actually unlink the account but forces the user to re-authenticate
      return { 
        success: false, 
        error: new Error('LinkedIn disconnect requires contacting support. Please reach out to our support team to unlink your LinkedIn account.') 
      };
    } catch (error) {
      console.error('Error disconnecting LinkedIn:', error);
      return { success: false, error };
    }
  }

  /**
   * Force refresh of LinkedIn authentication status
   */
  static async refreshLinkedInStatus(): Promise<{ success: boolean; error?: any }> {
    try {
      // Refresh the current session to get updated identity information
      const { data, error } = await supabase.auth.refreshSession();
      
      if (error) {
        throw error;
      }

      return { success: true };
    } catch (error) {
      console.error('Error refreshing LinkedIn status:', error);
      return { success: false, error };
    }
  }
}